import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  listBankAccounts,
  createBankAccount,
  deactivateBankAccount,
  updateBankAccount,
  type CreateBankAccountPayload,
  type UpdateBankAccountPayload,
} from '../../../api/bank-accounts';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

export function useBankAccounts() {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => listBankAccounts(getToken()),
  });
}

export function useCreateBankAccount() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateBankAccountPayload) =>
      createBankAccount(payload, getToken()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bank-accounts'] }),
  });
}

export function useUpdateBankAccount() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { readonly id: string; readonly payload: UpdateBankAccountPayload }) =>
      updateBankAccount(id, payload, getToken()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bank-accounts'] }),
  });
}

export function useDeactivateBankAccount() {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deactivateBankAccount(id, getToken()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bank-accounts'] }),
  });
}
